import React, { useState, createContext, useContext, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Toast Context
const ToastContext = createContext(null);

/** Purpose: Executes use toast. Inputs: none. Output: Operation result. */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast Provider
/** Purpose: Executes { children }. Inputs: { children }. Output: Operation result. */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
    remove: removeToast
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// Toast Container
/** Purpose: Renders toast container. Inputs: { toasts, onRemove }. Output: React render output (JSX). */
function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

// Individual Toast
/** Purpose: Renders toast item. Inputs: { toast, onRemove }. Output: React render output (JSX). */
function ToastItem({ toast, onRemove }) {
  const [isExiting, setIsExiting] = useState(false);

  /** Purpose: Handles remove. Inputs: none. Output: Operation result. */
  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 200);
  };

  const icons = {
    success: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    error: <XCircle className="w-4 h-4 text-red-500" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    info: <Info className="w-4 h-4 text-brand-500" />
  };

  const backgrounds = {
    success: 'bg-white border-emerald-200/60',
    error: 'bg-white border-red-200/60',
    warning: 'bg-white border-amber-200/60',
    info: 'bg-white border-brand-200/60'
  };

  const textColors = {
    success: 'text-slate-700',
    error: 'text-slate-700',
    warning: 'text-slate-700',
    info: 'text-slate-700'
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3.5 rounded-2xl border shadow-elevated backdrop-blur-sm
        ${backgrounds[toast.type]}
        ${isExiting ? 'animate-slide-out' : 'animate-slide-in'}
      `}
    >
      {icons[toast.type]}
      <p className={`flex-1 text-sm font-medium ${textColors[toast.type]}`}>
        {toast.message}
      </p>
      <button
        onClick={handleRemove}
        className="text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default ToastProvider;
